/** Raw PCM the Live API expects from the microphone. */
export const LIVE_INPUT_SAMPLE_RATE = 16_000;
/** Raw PCM the Live API returns for playback. */
export const LIVE_OUTPUT_SAMPLE_RATE = 24_000;
/** 512 samples at 16 kHz: about 32 ms per WebSocket audio message. */
export const LIVE_CAPTURE_CHUNK_SAMPLES = 512;
export const LIVE_WORKLET_NAME = "hrob-pcm16-16k";
const LIVE_WORKLET_URL = "/live-voice-worklet.js";
const MAX_PLAYBACK_SECONDS = 30;
const MAX_PLAYBACK_SOURCES = 256;
const PLAYBACK_LEAD_SECONDS = 0.01;
const PLAYBACK_EDGE_SECONDS = 0.004;
// Cushion before speech starts. The provider sends audio in bursts; with only the 10ms lead
// every late chunk left a gap with a fade out and in, heard as clicks.
// Запас перед початком мовлення: провайдер шле звук порціями, і запізніла порція з запасом
// лише 10мс лишала розрив із згасанням і наростанням — це чути як клацання.
const PLAYBACK_PREROLL_SECONDS = 0.12;
const PLAYBACK_PREROLL_MAX_SECONDS = 0.3;
const PLAYBACK_PREROLL_GROWTH = 1.5;
// A chunk arriving this soon after the queue ran dry continues the same reply.
// Частина, що прийшла так скоро після спорожнілої черги, продовжує ту саму відповідь.
const PLAYBACK_LATE_WINDOW_SECONDS = 1;

export type LiveAudio = {
  /** Unlock playback from the click that starts the session, before any await. */
  prepare(): Promise<void>;
  startCapture(
    onChunk: (chunk: Uint8Array) => void,
    onLevel: (level: number) => void,
  ): Promise<void>;
  stopCapture(): void;
  isCaptureActive(): boolean;
  play(chunk: Uint8Array): boolean;
  /** Whether decoded speech is still scheduled or fading out. */
  isPlaying?(): boolean;
  /** Subscribe to the actual end of queued playback. */
  onPlaybackIdle?(listener: () => void): () => void;
  /** Notify when an operating-system interruption prevents queued playback. */
  onPlaybackFault?(listener: () => void): () => void;
  stopPlayback(): void;
  setMuted(muted: boolean): void;
  close(): void;
  stats?(): LiveAudioPlaybackStats;
};

/** Local diagnostics: how often speech arrived too late to stay gapless. */
export type LiveAudioPlaybackStats = { underruns: number; prerollMs: number };

type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;
type MediaDevicesLike = Pick<MediaDevices, "getUserMedia">;

export type BrowserLiveAudioOptions = {
  audioContext?: AudioContextConstructor;
  mediaDevices?: MediaDevicesLike;
  workletUrl?: string;
  prerollSeconds?: number;
};

type Capture = {
  context: AudioContext;
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  node: AudioWorkletNode;
  sink: GainNode;
};

type PlaybackSource = {
  source: AudioBufferSourceNode;
  gain: GainNode;
  startAt: number;
  endAt: number;
  edgeSeconds: number;
  fadesIn: boolean;
  fadesOut: boolean;
};

/** Decode little-endian PCM16 bytes into the float samples an AudioBuffer wants. */
export function decodePcm16Le(bytes: Uint8Array): Float32Array {
  const length = Math.floor(bytes.byteLength / 2);
  const samples = new Float32Array(length);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let index = 0; index < length; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / 0x8000;
  }
  return samples;
}

/** Encode float samples as little-endian PCM16. Used by tests and future capture paths. */
export function encodePcm16Le(samples: Float32Array): Uint8Array {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index] ?? 0));
    view.setInt16(index * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return bytes;
}

/** Root-mean-square amplitude of 16-bit little-endian PCM, from 0 to 1. */
export function pcm16Rms(bytes: Uint8Array): number {
  const length = Math.floor(bytes.byteLength / 2);
  if (length === 0) return 0;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let sum = 0;
  for (let index = 0; index < length; index += 1) {
    const sample = view.getInt16(index * 2, true) / 0x8000;
    sum += sample * sample;
  }
  return Math.sqrt(sum / length);
}

export function pcm16Level(bytes: Uint8Array): number {
  return Math.min(1, pcm16Rms(bytes) * 4);
}

function tracksOf(stream: MediaStream): MediaStreamTrack[] {
  return typeof stream.getTracks === "function" ? stream.getTracks() : [];
}

/**
 * Browser audio for a live session. Playback uses a 24 kHz AudioBuffer over the
 * default output context, so it also works where the browser refuses a custom
 * sample rate. Capture uses an AudioWorklet and falls back to the context's own
 * rate inside the worklet.
 */
export class BrowserLiveAudio implements LiveAudio {
  private readonly options: BrowserLiveAudioOptions;
  private capture: Capture | null = null;
  private captureGeneration = 0;
  private playback: AudioContext | null = null;
  private playbackOutput: AudioNode | null = null;
  private sources = new Map<AudioBufferSourceNode, PlaybackSource>();
  private retiring = new Set<PlaybackSource>();
  private idleListeners = new Set<() => void>();
  private faultListeners = new Set<() => void>();
  private tail: PlaybackSource | null = null;
  private nextPlayAt = 0;
  private readonly basePreroll: number;
  private preroll: number;
  private underruns = 0;
  private muted = false;
  private closed = false;

  constructor(options: BrowserLiveAudioOptions = {}) {
    this.options = options;
    this.basePreroll = Math.max(
      PLAYBACK_LEAD_SECONDS,
      options.prerollSeconds ?? PLAYBACK_PREROLL_SECONDS,
    );
    this.preroll = this.basePreroll;
  }

  stats(): LiveAudioPlaybackStats {
    return { underruns: this.underruns, prerollMs: Math.round(this.preroll * 1000) };
  }

  isPlaying(): boolean {
    return this.sources.size > 0 || this.retiring.size > 0;
  }

  onPlaybackIdle(listener: () => void): () => void {
    this.idleListeners.add(listener);
    return () => this.idleListeners.delete(listener);
  }

  onPlaybackFault(listener: () => void): () => void {
    this.faultListeners.add(listener);
    return () => this.faultListeners.delete(listener);
  }

  async prepare(): Promise<void> {
    if (this.closed) return;
    const context = this.playbackContext();
    if (context.state !== "running" && context.state !== "closed") await context.resume();
  }

  async startCapture(
    onChunk: (chunk: Uint8Array) => void,
    onLevel: (level: number) => void,
  ): Promise<void> {
    if (this.capture || this.closed) return;
    const generation = ++this.captureGeneration;
    const mediaDevices = this.mediaDevices();
    const stream = await mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    if (this.closed || generation !== this.captureGeneration) {
      for (const track of tracksOf(stream)) track.stop();
      return;
    }
    const context = this.playbackContext();
    try {
      if (!context.audioWorklet) throw new Error("AudioWorklet is not available");
      await context.audioWorklet.addModule(this.options.workletUrl ?? LIVE_WORKLET_URL);
      if (this.closed || generation !== this.captureGeneration) {
        for (const track of tracksOf(stream)) track.stop();
        return;
      }
      const source = context.createMediaStreamSource(stream);
      if (typeof AudioWorkletNode === "undefined") {
        throw new Error("AudioWorklet is not available");
      }
      const node = new AudioWorkletNode(context, LIVE_WORKLET_NAME, {
        processorOptions: { chunkSamples: LIVE_CAPTURE_CHUNK_SAMPLES },
      });
      const sink = context.createGain();
      sink.gain.value = 0;
      node.port.onmessage = (event: MessageEvent) => {
        const data: unknown = event.data;
        const bytes =
          data instanceof ArrayBuffer
            ? new Uint8Array(data)
            : data instanceof Uint8Array
              ? data
              : null;
        if (!bytes) return;
        if (this.muted) {
          onLevel(0);
          return;
        }
        onChunk(bytes);
        onLevel(pcm16Level(bytes));
      };
      source.connect(node);
      node.connect(sink);
      sink.connect(context.destination);
      this.capture = { context, stream, source, node, sink };
      if (context.state !== "running" && context.state !== "closed") await context.resume();
    } catch (error) {
      for (const track of tracksOf(stream)) track.stop();
      throw error;
    }
  }

  stopCapture(): void {
    this.captureGeneration++;
    const capture = this.capture;
    this.capture = null;
    if (!capture) return;
    capture.node.port.onmessage = null;
    try {
      capture.source.disconnect();
      capture.node.disconnect();
      capture.sink.disconnect();
    } catch {
      // A partially connected graph can throw; the tracks still need stopping.
    }
    for (const track of tracksOf(capture.stream)) track.stop();
    // The capture and playback contexts are the same, unlocked by the start
    // gesture; closing it here would silence a session that is reconnecting.
  }

  isCaptureActive(): boolean {
    return (
      this.capture?.context.state === "running" &&
      tracksOf(this.capture.stream).some((track) => track.readyState === "live" && !track.muted)
    );
  }

  play(chunk: Uint8Array): boolean {
    if (this.closed || chunk.byteLength < 2) return false;
    const context = this.playbackContext();
    // A suspended clock cannot consume queued speech. Resume via the session recovery path.
    // Призупинений годинник не споживає чергу звуку; поновлення виконує сесія.
    if (context.state !== "running") return false;
    const now = context.currentTime;
    let startAt = this.nextPlayAt;
    if (startAt - now < PLAYBACK_LEAD_SECONDS) {
      // The queue ran dry. nextPlayAt survives onended, so it tells a late chunk of the same reply
      // from the start of a new one; a late chunk widens the cushion for the chunks behind it.
      // Черга спорожніла. nextPlayAt переживає onended, тому відрізняє запізнілу частину тієї ж
      // відповіді від початку нової; запізніла частина розширює запас для наступних.
      const late = this.nextPlayAt > 0 && now - this.nextPlayAt <= PLAYBACK_LATE_WINDOW_SECONDS;
      if (late) {
        this.underruns += 1;
        this.preroll = Math.min(
          this.preroll * PLAYBACK_PREROLL_GROWTH,
          PLAYBACK_PREROLL_MAX_SECONDS,
        );
      } else this.preroll = this.basePreroll;
      startAt = now + this.preroll;
    }
    const duration = Math.floor(chunk.byteLength / 2) / LIVE_OUTPUT_SAMPLE_RATE;
    if (
      startAt - context.currentTime + duration > MAX_PLAYBACK_SECONDS ||
      this.sources.size + this.retiring.size >= MAX_PLAYBACK_SOURCES
    )
      return false;
    const samples = decodePcm16Le(chunk);
    const buffer = context.createBuffer(1, samples.length, LIVE_OUTPUT_SAMPLE_RATE);
    buffer.getChannelData(0).set(samples);
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gain = context.createGain();
    const previous = this.tail;
    const contiguous = previous !== null && Math.abs(previous.endAt - startAt) < 1e-7;
    if (contiguous) {
      // The 10ms lead guarantees cancellation precedes the <=4ms tail fade.
      // Запас 10мс гарантує скасування до початку згасання тривалістю до 4мс.
      const fadeAt = previous.endAt - previous.edgeSeconds;
      previous.gain.gain.cancelScheduledValues(fadeAt);
      if (previous.fadesIn && Math.abs(fadeAt - (previous.startAt + previous.edgeSeconds)) < 1e-7)
        previous.gain.gain.linearRampToValueAtTime(1, fadeAt);
      else previous.gain.gain.setValueAtTime(1, fadeAt);
      previous.fadesOut = false;
    }
    const entry: PlaybackSource = {
      source,
      gain,
      startAt,
      endAt: startAt + buffer.duration,
      edgeSeconds: Math.min(PLAYBACK_EDGE_SECONDS, buffer.duration / 2),
      fadesIn: !contiguous,
      fadesOut: true,
    };
    gain.gain.setValueAtTime(contiguous ? 1 : 0, startAt);
    if (!contiguous) gain.gain.linearRampToValueAtTime(1, startAt + entry.edgeSeconds);
    gain.gain.setValueAtTime(1, entry.endAt - entry.edgeSeconds);
    gain.gain.linearRampToValueAtTime(0, entry.endAt);
    source.connect(gain);
    gain.connect(this.playbackOutput ?? context.destination);
    this.nextPlayAt = entry.endAt;
    this.tail = entry;
    this.sources.set(source, entry);
    source.onended = () => {
      this.disconnectSource(entry);
      this.notifyPlaybackIdle();
    };
    source.start(startAt);
    return true;
  }

  stopPlayback(): void {
    const hadPlayback = this.isPlaying();
    const context = this.playback;
    const now = context?.currentTime ?? 0;
    const canFade = !this.closed && context?.state === "running";
    if (!canFade) {
      for (const entry of [...this.sources.values(), ...this.retiring]) {
        try {
          entry.source.stop();
        } catch {
          /* Already ended. / Уже завершено. */
        }
        this.disconnectSource(entry);
      }
    } else {
      for (const entry of this.sources.values()) {
        if (now <= entry.startAt || now >= entry.endAt) {
          try {
            entry.source.stop(now);
          } catch {
            /* Already ended. / Уже завершено. */
          }
          this.disconnectSource(entry);
          continue;
        }
        const gain = entry.gain.gain;
        const fromStart = entry.fadesIn
          ? Math.min(1, (now - entry.startAt) / entry.edgeSeconds)
          : 1;
        const toEnd = entry.fadesOut ? Math.min(1, (entry.endAt - now) / entry.edgeSeconds) : 1;
        gain.cancelScheduledValues(now);
        gain.setValueAtTime(Math.max(0, Math.min(fromStart, toEnd)), now);
        const stopAt = Math.min(now + PLAYBACK_EDGE_SECONDS, entry.endAt);
        gain.linearRampToValueAtTime(0, stopAt);
        try {
          entry.source.stop(stopAt);
          this.retiring.add(entry);
        } catch {
          this.disconnectSource(entry);
        }
      }
    }
    this.sources.clear();
    this.tail = null;
    this.nextPlayAt = 0;
    this.preroll = this.basePreroll;
    if (hadPlayback) this.notifyPlaybackIdle();
  }

  private notifyPlaybackIdle(): void {
    if (this.isPlaying() || this.closed) return;
    // Finish the interrupt/stop transition before a waiting answer is considered.
    // Завершити переривання перед перевіркою відповіді, що очікує в черзі.
    queueMicrotask(() => {
      if (this.isPlaying() || this.closed) return;
      for (const listener of this.idleListeners) listener();
    });
  }

  private disconnectSource(entry: PlaybackSource): void {
    this.sources.delete(entry.source);
    this.retiring.delete(entry);
    if (this.tail === entry) this.tail = null;
    entry.source.onended = null;
    entry.source.disconnect();
    entry.gain.disconnect();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  close(): void {
    this.closed = true;
    this.idleListeners.clear();
    this.faultListeners.clear();
    this.stopCapture();
    this.stopPlayback();
    const playback = this.playback;
    this.playback = null;
    this.playbackOutput = null;
    if (playback) void playback.close().catch(() => undefined);
  }

  private playbackContext(): AudioContext {
    if (this.playback && this.playback.state !== "closed") return this.playback;
    // A replacement context starts a new clock; discard the closed context's queued audio.
    // Новий контекст має власний годинник; чергу закритого контексту потрібно скинути.
    if (this.playback) this.stopPlayback();
    const context = new (this.audioContextConstructor())({ latencyHint: "interactive" });
    this.playback = context;
    if (typeof context.addEventListener === "function") {
      context.addEventListener("statechange", () => {
        if (
          this.playback === context &&
          !this.closed &&
          context.state !== "running" &&
          this.isPlaying()
        )
          for (const listener of this.faultListeners) listener();
      });
    }
    // One continuous dynamics stage, never per-chunk gain changes that cause pumping.
    // Один безперервний обробник динаміки замість стрибків підсилення кожного фрагмента.
    if (typeof context.createDynamicsCompressor === "function") {
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -20;
      compressor.knee.value = 12;
      compressor.ratio.value = 3;
      compressor.attack.value = 0.01;
      compressor.release.value = 0.25;
      compressor.connect(context.destination);
      this.playbackOutput = compressor;
    } else this.playbackOutput = null;
    return context;
  }

  private audioContextConstructor(): AudioContextConstructor {
    if (this.options.audioContext) return this.options.audioContext;
    if (typeof AudioContext === "function") return AudioContext;
    throw new Error("This browser has no audio support.");
  }

  private mediaDevices(): MediaDevicesLike {
    if (this.options.mediaDevices) return this.options.mediaDevices;
    if (typeof navigator !== "undefined" && navigator.mediaDevices) return navigator.mediaDevices;
    throw new Error("This browser has no microphone support.");
  }
}
